import { useRoutes, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { BusinessApp } from "@rewardle/apps.businessapp";
import { authedRoutes, unAuthedRoutes, unAuthorisedlayout } from "./routes";
import { firebaseConfig, firebaseConfigForProduction } from "./config";
import { useOrdersStore } from "./stores/orders/orders-store";
import { fetchMerchantInfo } from "./utils/api";
import { useAuthState } from "./hooks/useAuthState";

const baseUrl: any = process.env.REACT_APP_API_BASE_MENU_URL;

const firebaseConfigData =
  process?.env?.REACT_APP_IS_LIVE === "true"
    ? firebaseConfigForProduction
    : firebaseConfig;

function App() {
  const {
    setUserInteracted,
    hasInteracted,
    setBusinessId,
    setBusinessName,
    debugBusinessId
  } = useOrdersStore();
  const [userDetails, setUserDetails] = useState<any>(null);

  // Use custom hook to handle authentication state changes
  useAuthState({ userDetails });

  // Function to handle business info fetch after login
  const handleBusinessInfoFetch = async (userDetails: any) => {
    if (userDetails?.token) {
      try {
        const businessData = await fetchMerchantInfo(userDetails.token);
        console.log("Business data:", businessData);

        // Use first business object from the response
        if (businessData && businessData.length > 0) {
          const { BusinessId, BusinessName } = businessData[0];
          if (BusinessId) {
            setBusinessId(BusinessId);
            if (BusinessName) {
              setBusinessName(BusinessName);
            }
            console.log("BusinessId set as business ID:", BusinessId);
            console.log("BusinessName set as:", BusinessName);
            setTimeout(() => debugBusinessId(), 100);
          } else {
            console.warn(
              "No BusinessId found in business data:",
              businessData[0]
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch business info:", error);
      }
    }
  };

  // Effect to call business API when userDetails changes
  useEffect(() => {
    if (
      userDetails?.token &&
      userDetails?.isAuthenticated &&
      userDetails?.isAuthorized
    ) {
      handleBusinessInfoFetch(userDetails);
    }
  }, [userDetails]);

  const renderRoutes = () => {
    let pageRoutes = [];
    const authenticatedRoutes = authedRoutes.map((route) => ({
      ...route,
      element: React.cloneElement(route.element, { userDetails })
    }));

    pageRoutes = userDetails?.token
      ? userDetails?.isAuthenticated && userDetails?.isAuthorized
        ? authenticatedRoutes
        : unAuthorisedlayout
      : unAuthedRoutes;

    return pageRoutes;
  };

  const routes = useRoutes(renderRoutes());
  const navigate = useNavigate();

  useEffect(() => {
    const handleFirstInteraction = async () => {
      if (hasInteracted) return;

      try {
        const audio = new Audio(
          "https://s3-ap-southeast-2.amazonaws.com/rewardlelivedata/default/mob.ua-iphone_crack_short.mp3"
        );

        // Set initial properties before attempting to play
        audio.muted = true;
        audio.volume = 0.5;
        audio.preload = "auto";

        // Try to load the audio first with timeout
        await Promise.race([
          new Promise((resolve, reject) => {
            audio.addEventListener("canplaythrough", resolve, { once: true });
            audio.addEventListener("error", reject, { once: true });
            audio.load();
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Audio load timeout")), 5000)
          )
        ]);

        // Now attempt to play (should work since it's muted)
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;

        setUserInteracted(audio);
        console.log("✅ Audio initialized successfully");
      } catch (error) {
        console.error("Failed to initialize audio:", error);
        // Try to create a simple audio element as fallback
        try {
          const fallbackAudio = new Audio();
          fallbackAudio.muted = true;
          fallbackAudio.volume = 0.5;
          // Try to play a simple beep sound
          const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);

          setUserInteracted(fallbackAudio);
          console.log("✅ Fallback audio initialized successfully");
        } catch (fallbackError) {
          console.error("Fallback audio also failed:", fallbackError);
          // Still set user as interacted to prevent infinite retries
          setUserInteracted(new Audio());
        }
        return;
      }

      // Only remove event listeners on success
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
    };

    window.addEventListener("click", handleFirstInteraction);
    window.addEventListener("keydown", handleFirstInteraction);

    return () => {
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
    };
  }, [hasInteracted, setUserInteracted]);

  return (
    <>
      <BusinessApp
        firebaseConfig={firebaseConfigData}
        apiDomain={baseUrl}
        navigate={navigate}
        setUserDetails={setUserDetails}
        isShowMenus={false}
        isShowDrawer={false}
      >
        {routes}
      </BusinessApp>
    </>
  );
}

export default App;
